import { faqs } from "@/content/faq";
import { organizationSchema, site } from "@/content/site";

function canonical(path: string) {
  return `${site.url}${path === "/" ? "/" : path}`;
}

function breadcrumb(path: string, name: string) {
  const parts = [
    { name: "Home", path: "/" },
    { name, path },
  ];
  const lastIndex = parts.length - 1;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: parts.map((part, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: part.name,
      item: canonical(part.path),
      ...(index === lastIndex ? {} : {}),
    })),
  };
}

function webPage(name: string, description: string, path: string) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name,
    description,
    url: canonical(path),
    isPartOf: { "@type": "WebSite", name: site.name, url: site.url },
  };
}

function organizationReference() {
  return { "@type": "Organization", name: site.name, url: site.url, email: site.email };
}

export function buildSchemaForRoute(input: {
  path: string;
  title: string;
  description: string;
}): unknown {
  const organization = organizationSchema;

  switch (input.path) {
    case "/":
      return [
        organization,
        {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: site.name,
          url: site.url,
          description: input.description,
          publisher: organizationReference(),
        },
      ];
    case "/product":
      return [
        organization,
        webPage(input.title, input.description, input.path),
        breadcrumb(input.path, "Product"),
        {
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: site.name,
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: `${site.url}/product`,
          description: input.description,
          publisher: organizationReference(),
        },
      ];
    case "/how-it-works":
    case "/use-cases":
    case "/about":
    case "/security":
    case "/privacy":
    case "/terms":
      return [
        organization,
        webPage(input.title, input.description, input.path),
        breadcrumb(input.path, input.title.replace(" | Agentic Plus", "")),
      ];
    case "/faq":
      return [
        organization,
        webPage(input.title, input.description, input.path),
        breadcrumb(input.path, "FAQ"),
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: { "@type": "Answer", text: faq.answer },
          })),
        },
      ];
    case "/contact":
      return [
        organization,
        webPage(input.title, input.description, input.path),
        breadcrumb(input.path, "Contact"),
        {
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: input.title,
          description: input.description,
          url: canonical(input.path),
          mainEntity: {
            ...organizationReference(),
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "sales",
              email: site.email,
              availableLanguage: ["English"],
            },
          },
        },
      ];
    default:
      return organization;
  }
}
